import type React from "react";
import { useEffect, useRef, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";

import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import { AgentHelpLink } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/components/AgentHelpLink/AgentHelpLink";
import { AgentKnowledgeBaseButton } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/components/AgentKnowledgeBaseButton/AgentKnowledgeBaseButton";
import { AgentKnowledgeBaseModal } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/components/AgentKnowledgeBaseModal/AgentKnowledgeBaseModal";
import { aiAgentKnowledgeBaseQueryKeys } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/queries/queryKeyFactory";
import { proNavigator } from "single-spa/react-app/ProNavigator";

type Props = {
    children?: React.ReactNode;
    helpLinkHref?: string;
    position?: "absolute" | "sticky" | "relative";
    top?: number;
};

export const AgentKnowledgeBase = ({ children, helpLinkHref, position = "absolute", top = 26 }: Props) => {
    const {
        params: { isSettingsOpen, agentSettingsOpen },
    } = useCurrentStateAndParams<{ isSettingsOpen?: boolean; agentSettingsOpen?: boolean }>();
    const queryClient = useQueryClient();

    const [isKnowledgeBaseOpened, setIsKnowledgeBaseOpened] = useState(!!isSettingsOpen || !!agentSettingsOpen);
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isSettingsOpen) {
            setIsKnowledgeBaseOpened(true);
            void queryClient.invalidateQueries({ queryKey: aiAgentKnowledgeBaseQueryKeys.customization() });
            contentRef.current?.scrollTo(0, 0);
            void proNavigator.updateParams({ isSettingsOpen: undefined });
        }
    }, [isSettingsOpen]);

    const handleOpenModal = () => {
        setIsKnowledgeBaseOpened(true);
        AIAgentTrackingService.kb.trackOpenModal();
    };

    return (
        <>
            <Flex
                position={position}
                top={position === "relative" ? undefined : `${top}px`}
                right={position === "absolute" ? "32px" : undefined}
                alignItems="center"
                alignSelf="flex-end"
                gap="8px"
                zIndex={1}
                margin={position === "sticky" ? "0 32px 0 0" : 0}
            >
                {children}
                <AgentKnowledgeBaseButton
                    onClick={handleOpenModal}
                    text={translate("si.agent_knowledge_base.button.meeting.agent")}
                />
                {helpLinkHref ? <AgentHelpLink href={helpLinkHref} /> : null}
            </Flex>
            <AgentKnowledgeBaseModal
                isOpen={isKnowledgeBaseOpened}
                onClose={() => setIsKnowledgeBaseOpened(false)}
                contentRef={contentRef}
            />
        </>
    );
};
